<template>
<div>
  <navigation></navigation>
  <div class="cart-page">
    <div class="container">
      <h1 class="page-title">Shopping Cart</h1>

      <div v-if="cart.length === 0" class="empty-cart">
        <div class="empty-icon">🛒</div>
        <h2>Your cart is empty</h2>
        <p>Add some items from our shop to get started!</p>
        <button class="btn-continue-shopping" @click="$router.push('/shop')">
          Continue Shopping
        </button>
      </div>

      <div v-else class="cart-content">
        <div class="cart-items">
          <div
            v-for="item in cart"
            :key="item.variantId"
            class="cart-item"
          >
            <div class="item-image">
              <img
                v-if="item.image"
                :src="item.image"
                :alt="item.name"
              />
              <div v-else class="no-image">No Image</div>
            </div>

            <div class="item-details">
              <h3 class="item-name">{{ item.name }}</h3>
              <div class="item-variants">
                <span v-if="item.color" class="variant-badge">{{ item.color }}</span>
                <span v-if="item.size" class="variant-badge">{{ item.size }}</span>
              </div>
              <div class="item-price">
                <span v-if="item.isOnSale" class="sale-indicator">SALE</span>
                ${{ formatPrice(item.price) }}
              </div>
            </div>

            <div class="item-actions">
              <div class="quantity-control">
                <button
                  @click="decrementQuantity(item)"
                  :disabled="item.quantity <= 1"
                >
                  -
                </button>
                <input
                  v-model.number="item.quantity"
                  type="number"
                  min="1"
                  @change="updateQuantity(item)"
                />
                <button @click="incrementQuantity(item)">+</button>
              </div>

              <div class="item-total">
                ${{ formatPrice(item.price * item.quantity) }}
              </div>

              <button
                class="btn-remove"
                @click="removeItem(item)"
                title="Remove item"
              >
                ×
              </button>
            </div>
          </div>
        </div>

        <div class="cart-summary">
          <div class="summary-card">
            <h2>Order Summary</h2>

            <div class="summary-row">
              <span>Subtotal ({{ cartItemCount }} items)</span>
              <span>${{ formatPrice(cartTotal) }}</span>
            </div>

            <div class="summary-row">
              <span>Shipping</span>
              <span class="free-shipping">FREE</span>
            </div>

            <div class="summary-divider"></div>

            <div class="summary-row total">
              <span>Total</span>
              <span>${{ formatPrice(cartTotal) }}</span>
            </div>

            <button
              v-if="isAuthenticated"
              class="btn-checkout"
              @click="$router.push('/checkout')"
            >
              Proceed to Checkout
            </button>

            <button
              v-else
              class="btn-login"
              @click="$router.push('/login')"
            >
              Login to Checkout
            </button>

            <button
              class="btn-continue-shopping"
              @click="$router.push('/shop')"
            >
              Continue Shopping
            </button>

            <div v-if="!isAuthenticated" class="auth-notice">
              Please <router-link to="/login">login</router-link> to complete your purchase.
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import Nav from '../components/Nav'

export default {
  name: 'Cart',
  components: {
    'navigation': Nav
  },
  computed: {
    ...mapState(['cart', 'isAuthenticated']),
    ...mapGetters(['cartItemCount', 'cartTotal'])
  },
  methods: {
    incrementQuantity (item) {
      this.$store.dispatch('updateCartItem', {
        variantId: item.variantId,
        quantity: item.quantity + 1
      })
    },
    decrementQuantity (item) {
      if (item.quantity > 1) {
        this.$store.dispatch('updateCartItem', {
          variantId: item.variantId,
          quantity: item.quantity - 1
        })
      }
    },
    updateQuantity (item) {
      if (item.quantity < 1) {
        item.quantity = 1
      }
      this.$store.dispatch('updateCartItem', {
        variantId: item.variantId,
        quantity: item.quantity
      })
    },
    removeItem (item) {
      if (confirm(`Remove ${item.name} from cart?`)) {
        this.$store.dispatch('removeFromCart', item.variantId)
      }
    },
    formatPrice (price) {
      return price.toFixed(2)
    }
  }
}
</script>

<style scoped>
.cart-page {
  padding: 20px 0;
  min-height: 60vh;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.page-title {
  font-size: 32px;
  font-weight: bold;
  margin-bottom: 30px;
  text-align: center;
}

/* Empty Cart State */
.empty-cart {
  text-align: center;
  padding: 60px 20px;
}

.empty-icon {
  font-size: 80px;
  margin-bottom: 40px;
}

.empty-cart h2 {
  font-size: 24px;
  margin-bottom: 12px;
  color: #2d3748;
}

.empty-cart p {
  font-size: 16px;
  color: #718096;
  margin-bottom: 24px;
}

/* Cart Content Layout */
.cart-content {
  display: grid;
  grid-template-columns: 1fr 400px;
  gap: 30px;
  margin-top: 20px;
}

/* Cart Items */
.cart-items {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.cart-item {
  display: grid;
  grid-template-columns: 120px 1fr auto;
  gap: 20px;
  padding: 20px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  align-items: center;
}

.item-image {
  width: 120px;
  height: 120px;
  background: #f7fafc;
  border-radius: 4px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.item-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.no-image {
  color: #a0aec0;
  font-size: 14px;
}

.item-details {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.item-name {
  font-size: 18px;
  font-weight: 600;
  margin: 0;
  color: #2d3748;
}

.item-variants {
  display: flex;
  gap: 8px;
}

.variant-badge {
  padding: 4px 12px;
  background: #edf2f7;
  border-radius: 4px;
  font-size: 14px;
  color: #4a5568;
}

.item-price {
  font-size: 16px;
  font-weight: 600;
  color: #2b6cb0;
}

.sale-indicator {
  background: #e53e3e;
  color: white;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 10px;
  font-weight: bold;
  margin-right: 4px;
}

.item-actions {
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: flex-end;
}

.quantity-control {
  display: flex;
  align-items: center;
  gap: 8px;
}

.quantity-control button {
  width: 32px;
  height: 32px;
  border: 1px solid #cbd5e0;
  background: white;
  border-radius: 4px;
  cursor: pointer;
  font-size: 18px;
  transition: background 0.2s;
}

.quantity-control button:hover:not(:disabled) {
  background: #f7fafc;
}

.quantity-control button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.quantity-control input {
  width: 50px;
  height: 32px;
  text-align: center;
  border: 1px solid #cbd5e0;
  border-radius: 4px;
  font-size: 14px;
}

.item-total {
  font-size: 18px;
  font-weight: bold;
  color: #2d3748;
}

.btn-remove {
  background: transparent;
  border: none;
  color: #e53e3e;
  font-size: 32px;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  line-height: 1;
  transition: color 0.2s;
}

.btn-remove:hover {
  color: #c53030;
}

/* Cart Summary */
.cart-summary {
  position: sticky;
  top: 20px;
  height: fit-content;
}

.summary-card {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 24px;
}

.summary-card h2 {
  font-size: 20px;
  font-weight: bold;
  margin: 0 0 20px 0;
  color: #2d3748;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
  font-size: 16px;
  color: #4a5568;
}

.summary-row.total {
  font-size: 20px;
  font-weight: bold;
  color: #2d3748;
}

.free-shipping {
  color: #48bb78;
  font-weight: 600;
}

.summary-divider {
  height: 1px;
  background: #e2e8f0;
  margin: 16px 0;
}

.btn-checkout,
.btn-login,
.btn-continue-shopping {
  width: 100%;
  padding: 14px 24px;
  border: none;
  border-radius: 4px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 12px;
}

.btn-checkout {
  background: #48bb78;
  color: white;
}

.btn-checkout:hover {
  background: #38a169;
}

.btn-login {
  background: #3182ce;
  color: white;
}

.btn-login:hover {
  background: #2c5aa0;
}

.btn-continue-shopping {
  background: #e2e8f0;
  color: #2d3748;
}

.btn-continue-shopping:hover {
  background: #cbd5e0;
}

.auth-notice {
  margin-top: 16px;
  padding: 12px;
  background: #fef5e7;
  border-left: 4px solid #f39c12;
  border-radius: 4px;
  font-size: 14px;
}

.auth-notice a {
  color: #2b6cb0;
  font-weight: 600;
}

/* Responsive */
@media (max-width: 968px) {
  .cart-content {
    grid-template-columns: 1fr;
  }

  .cart-summary {
    position: static;
  }
}

@media (max-width: 768px) {
  .page-title {
    font-size: 24px;
  }

  .cart-item {
    grid-template-columns: 80px 1fr;
    gap: 12px;
  }

  .item-image {
    width: 80px;
    height: 80px;
  }

  .item-actions {
    grid-column: 1 / -1;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }

  .item-name {
    font-size: 16px;
  }

  .item-price {
    font-size: 14px;
  }
}
</style>



// WEBPACK FOOTER //
// Cart.vue?589bde96